import { ApiCreditcard } from '@/models/api/creditcard'
import { AvailableMembershipPlan } from '@/models/api/paid-member-price/member-plan/available-membership-plan'
import { getCreditcardPaymentProvierServiceConfig } from '@/services/payment'
import { CreditcardPaymentProviderServiceForSubscription } from '@/services/payment/creditcard-payment-provider/creditcard-payment-provider-service'
import { Payment } from '@spa/store/modules/orderConfirm/types'
import { User } from '@spa/store/modules/user/types'
import EventEmitter from 'eventemitter3'
import { identity } from 'fp-ts/lib/function'
import { none, Option, some, Some } from 'fp-ts/lib/Option'

/**
 * 有料会員登録は以下のステップとなる
 * 1-1. クレカ選択（登録済みの場合）
 * 1-2. クレカ登録（未登録もしくは変更の場合）
 * 2. プラン選択＆申し込み
 */
export type SubscriptionStep = 'show-creditcard' | 'input-creditcard' | 'select-plan' | 'completed'

interface CompletedInteraction {
  readonly selectedPlan: Some<string>
}

export class PaidMembershipSubscribeInteraction {
  public static initializeWithCreditCard(cardAlreadyRegistered: ApiCreditcard): PaidMembershipSubscribeInteraction {
    return new PaidMembershipSubscribeInteraction(
      some(cardAlreadyRegistered),
      [],
      none,
      'show-creditcard'
    )
  }
  public static empty(): PaidMembershipSubscribeInteraction {
    return new PaidMembershipSubscribeInteraction(
      none,
      [],
      none,
      'input-creditcard'
    )
  }

  private eventEmitter = new EventEmitter()

  private constructor(
    private _selectedCreditcard: Option<ApiCreditcard>,
    private _availablePlans: AvailableMembershipPlan[],
    private _selectedPlan: Option<string>,
    private _currentStep: SubscriptionStep,
    private readonly cardPaymentProvider: CreditcardPaymentProviderServiceForSubscription
      = getCreditcardPaymentProvierServiceConfig().forSubscription
  ) {
  }

  get selectedCreditCard(): Option<ApiCreditcard> {
    return this._selectedCreditcard
  }
  get existPlansSubscribableNewly(): boolean {
    return this.plansSubscribableNewly.length > 0
  }
  get plansSubscribableNewly(): AvailableMembershipPlan[] {
    return this._availablePlans.filter(plan => ! plan.subscribed())
  }
  get selectedPlan(): Option<string> {
    return this._selectedPlan
  }

  get isShowCreditcard(): boolean {
    return this._currentStep === 'show-creditcard'
  }

  get isInputCreditcard(): boolean {
    return this._currentStep === 'input-creditcard'
  }

  get isSelectPlan(): boolean {
    return this._currentStep === 'select-plan'
  }

  isCompleted(): this is CompletedInteraction {
    return this._currentStep === 'completed'
  }

  onStepChange(step: SubscriptionStep, f: () => void) {
    this.eventEmitter.on(step, f)
  }

  /**
   * クレジットカードを新しいカードで交換しようとする操作.
   *
   * この時点では交換の準備を始めただけで、まだカードの入れ替えは発生しない
   */
  willReplaceByNewCreditCard(): PaidMembershipSubscribeInteraction {
    this.changeStep('input-creditcard')

    return this
  }

  changeCreditCard(card: ApiCreditcard): PaidMembershipSubscribeInteraction {
    this._selectedCreditcard = some(card)
    this.changeStep('select-plan')

    return this
  }

  setAvailablePlans(plans: AvailableMembershipPlan[]): PaidMembershipSubscribeInteraction {
    this._availablePlans = plans

    return this
  }

  selectPlan(planId: string): PaidMembershipSubscribeInteraction {
    if (this._selectedCreditcard.isNone()) {
      throw new Error('credit card not selected')
    }

    const plan = this._availablePlans.find(p => p.externalId === planId) || null
    if (plan === null) {
      throw new Error(`plan ${planId} not exist in available plans`)
    }

    this._selectedPlan = some(planId)
    this.changeStep('completed')

    return this
  }

  /**
   * 有料会員登録時専用の決済パラメータを提供する
   */
  async providePaymentPayloadFor(user: User): Promise<Payment> {
    return this.selectedCreditCard.foldL(
      () => {
        throw new Error('any credit card not selected')
      },
      creditcard => this.cardPaymentProvider.createPaymentPayload(
        creditcard.withSecurityCode(''),
        user
      ).then(result => result.fold(
        (e) => {
          throw e
        },
        identity
      ))
    )
  }

  private changeStep(step: SubscriptionStep): void {
    this._currentStep = step

    this.eventEmitter.emit(this._currentStep)
  }
}
